import {types, flow, getSnapshot, destroy, applySnapshot, getRoot} from 'mobx-state-tree';
import {toJS} from 'mobx';
import _ from 'lodash';

import ContentItem from '../models/content-item';
import Category from '../models/category';
import {apiService} from '../services/api.service';
import {formUtilsService} from '../services/form-utils.service';

const ContentItems = types.model('ContentItems', {
  contentItemList: types.optional(types.array(ContentItem), []),
  categoryList: types.optional(types.array(Category), []),
  top: types.maybeNull(types.reference(ContentItem)),
  bottom: types.maybeNull(types.reference(ContentItem)),
  selectedContentItem: types.maybeNull(types.reference(ContentItem)),
  defaultContentItem: types.maybeNull(ContentItem),
  editingContentItem: types.maybeNull(ContentItem),
  addingContentItem: types.maybeNull(ContentItem),
  loadingOverlayCancelled: types.optional(types.boolean, false),
  showLoadingOverlay: types.optional(types.boolean, false),
  formWasTouched: types.optional(types.boolean, false)
})
.actions(self => ({
  fetchContentItems: flow(function* fetchRoles() {
    try {
      const result = yield apiService.getTrainingPortalContent();
      self.assignResultToSelf(result);
    } catch (error) {
      console.error('Failed to fetch content', error)
    }
  }),

  fetchContentForRole: flow(function* fetchContentForRole(role) {
    try {
      const result = yield apiService.getContentForRole(role);
      self.assignResultToSelf(result);
    } catch (error) {
      console.error(`Failed to fetch content for role ${role}`, error);
    }
  }),

  assignResultToSelf(result) {
    self.categoryList = result.categories;

    //Add content, item by item, to identify any malformed pieces without halting
    self.contentItemList = [];
    result.items.forEach(item => {
      try {
        self.contentItemList.push(item);
      } catch (e) {
        console.error(`Could not add "${item.title}" because a value was incorrectly formatted.`, e)
      }
    })

    try {
      self.top = result.items.find(item => item.type === 'TOP_BANNER').id;
    } catch (e) {
      console.error('Could not add "TOP BANNER" because a value was incorrectly formatted.', e)
    }

    try {
      self.bottom = result.items.find(item => item.type === 'BOTTOM_BANNER').id;
    } catch (e) {
      console.error('Could not add "BOTTOM BANNER" because a value was incorrectly formatted.', e)
    }
  },


  setFormWasTouched() {
    self.formWasTouched = true;
  },

  findCategory(id) {
    return self.categoryList.find(cat => cat.id === id)
  },

  getContentTypeFromSectionId(sectionId) {
    if (sectionId === 'top') return 'TOP_BANNER';
    if (sectionId === 'bottom') return 'BOTTOM_BANNER';
    const matchingCategory = self.findCategory(parseInt(sectionId));
    return matchingCategory ? matchingCategory.allowedItemType : 'DOCUMENT';
  },

  setupContentItemToAdd(sectionId) {
    let initialData = {
      id: 'ADDING',
      category: sectionId,
      type: self.getContentTypeFromSectionId(sectionId)
    }
    self.addingContentItem = ContentItem.create(initialData);
    self.defaultContentItem = getSnapshot(self.addingContentItem); //use as default for dirty check
  },

  setupContentItemToEdit(id) {
    self.selectedContentItem = id;
    self.editingContentItem = formUtilsService.createMutableModel(ContentItem, self.selectedContentItem, 'id', `EDITING-${id}`);
  },

  cancelSubmission() {
    self.loadingOverlayCancelled = true;
    self.showLoadingOverlay = false;
    clearTimeout(self.submissionTimeout);
  },

  applyLoadingOverlay(delayedFunction) {
    // Allow user time to cancel submission from the overlay
    self.loadingOverlayCancelled = false;
    self.showLoadingOverlay = true;

		self.submissionTimeout = setTimeout(() => {
      if (!self.loadingOverlayCancelled) {
        self.resolveLoadingOverlay(delayedFunction);
      }
    }, 3000);
  },

  resolveLoadingOverlay(delayedFunction) {
    try {
      delayedFunction().then(() => {
        self.showLoadingOverlay = false;
      })
    } catch(e) {
      self.showLoadingOverlay = false;
    }
  },

  pushContentItemCreation() {
    return new Promise((resolve, reject) => {
      let finalModel = {};
      const success = (response) => {
        finalModel = formUtilsService.createMutableModel(ContentItem, self.addingContentItem, 'id', response.id);
        self.addContentItemToList(finalModel);
        self.clearForm();
        resolve();
        return finalModel;
      }
      const fail = (e) => {
        reject();
        throw new Error(e);
      }
      const delayedFunction = () => {
        apiService.addContentItem(self.addingContentItem).then(success, fail);
      }
      self.applyLoadingOverlay(delayedFunction);
    })
  },

  pushContentItemEdits() {
    return new Promise((resolve, reject) => {
      let mutableModel = formUtilsService.createMutableModel(ContentItem, self.editingContentItem, 'id', self.selectedContentItem.id);
      const success = () => {
        self.updateContentInList(mutableModel);
        self.clearForm();
        resolve();
        return mutableModel;
      }
      const fail = (e) => {
        reject();
        throw new Error(e);
      }
      const delayedFunction = () => {
        apiService.editContentItem(mutableModel).then(success, fail);
      }
      self.applyLoadingOverlay(delayedFunction);
    })
  },

  pushContentItemDeletion(id) {
    const success = () => {
      const modelToDelete = self.contentItemList.find(item => item.id === id);
      self.removeContentItemFromList(modelToDelete);
      self.clearForm();
    }
    const fail = (e) => {
      throw new Error(e);
    }
    return apiService.deleteContentItem(id).then(success, fail);
  },

  addContentItemToList(model) {
    self.contentItemList.push(model);
  },

  updateContentInList(newModel) {
    try {
      let oldModel = self.contentItemList.find(item => item.id === newModel.id);
      applySnapshot(oldModel, getSnapshot(newModel));
    } catch(e) {
      throw new Error(e)
    }
  },

  removeContentItemFromList(model) {
    destroy(model);
  },

  clearForm() {
    self.selectedContentItem = null;
    self.addingContentItem = null;
    self.editingContentItem = null;
    self.defaultContentItem = null;
    self.formWasTouched = false;
  }
}))
.views(self => ({
  get sortedContentList() {
    return self.categoryList.map(category => {
      return {
        sectionId: category.id,
        sectionType: category.allowedItemType,
        sectionTitle: category.name,
        sectionItems: self.contentItemList.filter(contentItem => contentItem.category && contentItem.category.id === category.id).sort(function(a,b){
          if(a.title.toUpperCase() < b.title.toUpperCase()) { return -1; }
          if(a.title.toUpperCase() > b.title.toUpperCase()) { return 1; }
          return 0;
        })
      }
    })
  },

  get formIsDirty() {
    if(!self.editingContentItem && !self.addingContentItem) return false;
    else if(self.editingContentItem && self.formWasTouched) return true;
    else return self.addFormIsDirty;
  },

  get addFormIsDirty() {
    return !_.isEqual(
      _.omit(toJS(self.defaultContentItem), ['id']),
      _.omit(toJS(self.addingContentItem), ['id'])
    );
  },

  get authorizationOptionsList() {
    const stringifyRole = (authorization) => {
      return JSON.stringify({role: authorization.role, isPremierUser: authorization.isPremierUser})
    }
    return getRoot(self).Roles.rolesList.map(authorization => {
      return {title: authorization.description, value: stringifyRole(authorization)}
    })
  }
}))

export default ContentItems;
